import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useMe, useSettings, useUpdateSettings } from '../api/queries';
import { Modal } from '../ui';
import { dateLabel } from '../lib/time';
import { newestAnnounced, unseenReleases } from '../lib/releases';
import type { Release } from '../lib/releases';

/**
 * What changed since this person last looked (2026-09-25) — once, and only
 * what they have not seen: *"a release notes modal that will show only new
 * changes that user did not see from last time — only minor version update and
 * above, no patch updates there"*.
 *
 * Closing it any way at all — the button, ×, Escape, the link to every note —
 * is having seen it, and is remembered on the account rather than in this
 * browser, so the second device does not say it again.
 *
 * Never to somebody new (2026-09-28): *"Make sure we don't show new users
 * modal with release notes. We need to show only to those who really skipped
 * some changes"* — `unseenReleases` has the rule.
 */
export function WhatsNew(): ReactElement | null {
  const me = useMe();
  const settings = useSettings();
  const update = useUpdateSettings();
  const { pathname } = useLocation();
  const [closed, setClosed] = useState(false);
  // Not over the first steps, and not over the page that already shows it all.
  const elsewhere = ['/start', '/stop', '/release-notes'].some((p) => pathname.startsWith(p));
  if (closed || elsewhere || !me.data || !settings.data) return null;
  const fresh = unseenReleases(settings.data.releaseNotesSeen, me.data.returnedAt ?? null);
  if (fresh.length === 0) return null;

  const seen = () => {
    setClosed(true);
    update.mutate({ releaseNotesSeen: newestAnnounced() });
  };
  return (
    <Modal
      title="What’s new in Ptitim"
      onClose={seen}
      foot={
        <>
          <Link to="/release-notes" className="btn btn-quiet" onClick={seen}>
            All release notes
          </Link>
          <button type="button" className="btn btn-primary" onClick={seen}>
            Got it
          </button>
        </>
      }
    >
      <div className="stack-lg">
        {fresh.map((release) => (
          <ReleaseEntry key={release.version} release={release} />
        ))}
      </div>
    </Modal>
  );
}

/** One release: its version and day, what it is called, and what a person can now do. */
export function ReleaseEntry({ release }: { release: Release }): ReactElement {
  return (
    <article className="release" aria-label={`Ptitim ${release.version}`}>
      <p className="mono">
        {release.version} · {dateLabel(release.shipped)}
      </p>
      <h3 className="t-heading">{release.title}</h3>
      <ul className="release-changes">
        {release.changes.map((change) => (
          <li key={change}>{change}</li>
        ))}
      </ul>
    </article>
  );
}
